import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../redux-store";

const DEBUG_ENABLED = false;

interface ReduxStateObjectFormValidationField {
  id: string;
  isValid: boolean;
  errorMessage: string;
}
interface ReduxStateObjectFormValidationSection {
  id: string;
  fields: ReduxStateObjectFormValidationField[];
}

interface ReduxStateObjectFormValidationForm {
  id: string;
  sections: ReduxStateObjectFormValidationSection[];
}

export interface ReduxStateObjectFormValidation {
  forms: ReduxStateObjectFormValidationForm[];
}

export interface TargetFieldPayload {
  formId?: string;
  formSectionId?: string;
  fieldId: string;
}

export interface SetFieldIsValidPayload extends TargetFieldPayload {
  fieldIsValid: boolean;
  fieldErrorMessage: string;
}

export interface CreateFormPayload {
  formId: string;
  sectionsIds: string[];
}

export interface DeleteFormPayload {
  formId: string;
}

export interface DeleteSectionPayload {
  formId: string;
  sectionId: string;
}

const initialState: ReduxStateObjectFormValidation = {
  forms: [
    {
      id: "default_form",
      sections: [
        {
          id: "default_section",
          fields: [],
        },
      ],
    },
  ],
};

export const formValidationSlice = createSlice({
  name: "formValidation",
  initialState,

  reducers: {
    CreateForm: (state, action: PayloadAction<CreateFormPayload>) => {
      const formId = action.payload.formId;
      const sectionsIds = action.payload.sectionsIds;

      const form = { id: formId, sections: sectionsIds.map((sectionId) => ({ id: sectionId, fields: [] })) };
      const indexOfForm = state.forms.findIndex((x) => x.id === formId);
      if (indexOfForm === -1) state.forms.push(form);
      else state.forms[indexOfForm] = form;
    },

    SetFieldIsValid: (state, action: PayloadAction<SetFieldIsValidPayload>) => {
      const formId = action.payload.formId || "default_form";
      const sectionId = action.payload.formSectionId || "default_section";
      const fieldId = action.payload.fieldId;
      const fieldIsValid = action.payload.fieldIsValid;
      const errorMessage = action.payload.fieldErrorMessage;

      let indexOfForm = state.forms.findIndex((x) => x.id === formId);
      if (indexOfForm === -1) {
        state.forms.push({ id: formId, sections: [] });
        indexOfForm = state.forms.length - 1;
      }

      const form = state.forms[indexOfForm];
      let indexOfSection = form.sections.findIndex((x) => x.id === sectionId);
      if (indexOfSection === -1) {
        form.sections.push({ id: sectionId, fields: [] });
        indexOfSection = form.sections.length - 1;
      }

      const section = form.sections[indexOfSection];
      let indexOfField = section.fields.findIndex((x) => x.id === fieldId);

      if (indexOfField === -1) {
        section.fields.push({ id: fieldId, isValid: fieldIsValid, errorMessage });
      } else {
        if (section.fields[indexOfField].isValid !== fieldIsValid) section.fields[indexOfField].isValid = fieldIsValid;
        if (section.fields[indexOfField].errorMessage !== errorMessage)
          section.fields[indexOfField].errorMessage = errorMessage;
      }
    },

    DeleteForm: (state, action: PayloadAction<DeleteFormPayload>) => {
      const formId = action.payload.formId;

      const indexOfForm = state.forms.findIndex((x) => x.id === formId);
      if (indexOfForm !== -1) state.forms.splice(indexOfForm, 1);
    },

    DeleteSection: (state, action: PayloadAction<DeleteSectionPayload>) => {
      const formId = action.payload.formId;
      const sectionId = action.payload.sectionId;
      const indexOfForm = state.forms.findIndex((x) => x.id === formId);
      if (indexOfForm === -1) return;

      const indexOfSection = state.forms[indexOfForm].sections.findIndex((x) => x.id === sectionId);
      if (indexOfSection !== -1) state.forms[indexOfForm].sections.splice(indexOfSection, 1);
    },

    DeleteField: (state, action: PayloadAction<TargetFieldPayload>) => {
      const formId = action.payload.formId || "default_form";
      const sectionId = action.payload.formSectionId || "default_section";
      const fieldId = action.payload.fieldId;

      const indexOfForm = state.forms.findIndex((x) => x.id === formId);
      if (indexOfForm === -1) return;

      const form = state.forms[indexOfForm];
      const indexOfSection = form.sections.findIndex((x) => x.id === sectionId);
      if (indexOfSection === -1) return;

      const section = form.sections[indexOfSection];
      const indexOfField = section.fields.findIndex((x) => x.id === fieldId);
      if (indexOfField === -1) return;

      if (indexOfField !== -1) state.forms[indexOfForm].sections[indexOfSection].fields.splice(indexOfField, 1);
    },
  },
});

export const { CreateForm, SetFieldIsValid, DeleteForm, DeleteSection, DeleteField } = formValidationSlice.actions;

export const FormExists =
  (formId: string) =>
  (state: RootState): boolean | undefined => {
    let form = state.formValidation.forms.find((x) => x.id === formId);

    if (!form) return false;

    return true;
  };

export const GetFormIsValid =
  (formId: string) =>
  (state: RootState): boolean | undefined => {
    let form = state.formValidation.forms.find((x) => x.id === formId);

    if (!form) return false;

    let result: boolean | undefined = form.sections.every((x) => x.fields.every((x) => x.isValid));

    if (result) return true;
    else {
      if (DEBUG_ENABLED) {
        // debug
        form.sections.forEach((section) => {
          section.fields.forEach((field) => {
            if (!field.isValid) console.info("fvalid:Invalid => " + field.id + " on section " + section.id);
          });
        });
      }
      return false;
    }
  };

export const GetSectionIsValid =
  (formId: string, sectionId: string) =>
  (state: RootState): boolean | undefined => {
    const form = state.formValidation.forms.find((x) => x.id === formId);

    if (!form) return false;

    return form.sections.find((x) => x.id === sectionId)?.fields.every((x) => x.isValid);
  };

export const GetFieldIsValidAndErrorMessage =
  (formId: string, fieldId: string) =>
  (state: RootState): string | undefined => {
    const form = state.formValidation.forms.find((x) => x.id === formId);

    if (!form) return undefined;

    const field = form.sections.flatMap((x) => x.fields).find((x) => x.id === fieldId);

    if (!field) return "No field " + field + " found";

    return field.isValid ? undefined : field.errorMessage;
  };

export default formValidationSlice.reducer;
